import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import NodeFooter from './NodeFooter';
import MarkdownBody from './MarkdownBody';
import BubbleKebabMenu from './BubbleKebabMenu';

/**
 * Split preview/content text into the card's title (first line, leading
 * "# " stripped) and body (the rest). `isHeading` says whether that first
 * line was a markdown heading. Shared with the Log's rename dialog so its
 * placeholder shows exactly the title the card would fall back to.
 */
export function splitPreview(text) {
  const raw = text || "";
  const isHeading = /^#\s+/.test(raw);
  const displayText = raw.replace(/^#\s+/, '');
  const firstNewline = displayText.indexOf('\n');
  return {
    title: firstNewline > 0 ? displayText.substring(0, firstNewline) : displayText,
    body: firstNewline > 0 ? displayText.substring(firstNewline + 1).trim() : '',
    isHeading,
  };
}

const tombstoneStyle = {
  fontFamily: 'var(--sans)',
  fontSize: '0.95rem',
  fontStyle: 'italic',
  color: 'var(--text-muted)',
  marginBottom: '0.6rem',
};

// The tag a prompt-rooted node wears. Keys are identifiers ("read_thread");
// the tag is a word. Both read prompts are one thing to the user: Glean
// (#435, the name Peter gave the read).
const PROMPT_LABELS = {
  read: 'Glean',
  read_thread: 'Glean',
};
export const stripQuoteMarkers = (text) => (text || '')
  .replace(/[ \t]*\{quote(?:_ext)?:\d+\}[ \t]*/g, ' ')
  .replace(/ +\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const promptLabel = (key) => PROMPT_LABELS[key]
  || (key.charAt(0).toUpperCase() + key.slice(1)).replace(/_/g, ' ');

const Bubble = ({
  node,
  onClick,
  isHighlighted = false,
  leftAlign = false,
  actions = null,
  // References (#232) reuse the card but are not nodes: `footer`
  // replaces NodeFooter (no username link, no reply count) and `tag`
  // fills the tag slot (Pinned / prompt / Voice Note) with a source label.
  footer = null,
  tag = null,
}) => {
  // Detect voice notes via backend-provided has_original_audio flag
  const isVoiceNote = !!node.has_original_audio;
  const isPinned = !!node.pinned_at;
  const promptKey = node.prompt_key || null;
  const isTombstone = !!node.deleted;
  const isInaccessible = !!node.inaccessible;
  const isPlaceholder = isTombstone || isInaccessible;

  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const hoverHideTimerRef = useRef(null);

  const cancelHoverHide = () => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
  };

  // Keep the kebab on screen for a beat after the cursor leaves so a
  // user can still reach it across the gap between bubble and icon.
  const scheduleHoverHide = () => {
    cancelHoverHide();
    hoverHideTimerRef.current = setTimeout(() => {
      setHovered(false);
      hoverHideTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => cancelHoverHide, []);

  // Use full content if available; otherwise use preview. Quote markers
  // ({quote:12}, {quote_ext:34}) become cards on the node's own page; in
  // a card they would read as raw markup, so they are left out (a
  // gleaning's picks above a "Glean again" are all markers, #435).
  const text = stripQuoteMarkers(node.content || node.preview || "");

  // Extract title (first line, "# " stripped) and body (rest)
  const { title, body, isHeading } = splitPreview(text);

  // A user-given thread name (Log cards; the Log serializes it from the
  // thread root) takes the title slot. The entry's own first line is
  // skipped only when it was a markdown heading — the name replaces that
  // title. Plain text has no title to replace, so all of it stays as the
  // body.
  const threadName = (node.thread_name || "").trim();
  const heading = threadName || title;
  const previewBody = threadName && !isHeading ? text.trim() : body;

  // Only offer expand when there's full content AND the collapsed preview
  // actually hides something: title past 120 chars, body past 250 chars,
  // or body with >2 newline-separated lines (which would be line-clamped).
  const canExpand = !isPlaceholder && !!node.content && (
    title.length > 120 ||
    body.length > 250 ||
    body.split('\n').length > 2
  );

  // Compute children count – use node.child_count if available, else fallback to node.children.length.
  const childrenCount = typeof node.child_count !== "undefined"
    ? node.child_count
    : (node.children ? node.children.length : 0);

  const bubbleStyle = {
    padding: "1.6rem 1.8rem",
    margin: leftAlign ? "8px 0" : "8px auto",
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: "10px",
    cursor: isPlaceholder ? "default" : "pointer",
    maxWidth: "1000px",
    // Right-side reserve covers the always-outside kebab (8px margin +
    // 26px icon + 8px breathing room past the icon to match the visual
    // gap to the bubble's left). On wide viewports maxWidth caps below
    // 100%-30 so this doesn't change anything; on narrow viewports it
    // keeps the kebab on screen down to ~320px portrait.
    width: "calc(100% - 30px)",
    transition: "border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease",
    position: "relative",
  };

  const tagStyle = {
    fontFamily: "var(--sans)",
    fontSize: "0.65rem",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    padding: "3px 8px",
    borderRadius: "4px",
    marginLeft: "8px",
  };

  // Show kebab on hover (mouse), on focus (keyboard), or always on touch
  // devices (no hover). Computed in inline style + the touch-device fallback.
  const kebabAlwaysVisible = (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: none)').matches
  );
  const hasActions = !isPlaceholder && Array.isArray(actions) && actions.length > 0;
  const kebabVisible = hasActions && (kebabAlwaysVisible || hovered);

  const handleCardClick = (e) => {
    if (isPlaceholder) return; // Tombstones and inaccessible are non-navigable.
    if (onClick) onClick(node.id, e);
  };

  return (
    <div
      style={bubbleStyle}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        cancelHoverHide();
        setHovered(true);
        if (!isPlaceholder) {
          e.currentTarget.style.borderColor = 'var(--border-hover)';
          e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.3)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={(e) => {
        scheduleHoverHide();
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {hasActions && (
        <BubbleKebabMenu
          visible={kebabVisible}
          items={actions}
          onFocus={() => { cancelHoverHide(); setHovered(true); }}
          onBlur={() => scheduleHoverHide()}
        />
      )}
      {isTombstone ? (
        <div style={tombstoneStyle}>[Node deleted]</div>
      ) : isInaccessible ? (
        <div style={tombstoneStyle}>[Node inaccessible]</div>
      ) : expanded ? (
        <div style={{
          fontFamily: "var(--sans)",
          fontSize: "0.95rem",
          fontWeight: 300,
          color: "var(--text-secondary)",
          lineHeight: 1.7,
          marginBottom: "0.6rem",
        }}>
          <MarkdownBody>{stripQuoteMarkers(node.content)}</MarkdownBody>
        </div>
      ) : (
        <>
          <div style={{
            fontFamily: "var(--sans)",
            fontSize: "1rem",
            color: "var(--text-primary)",
            marginBottom: previewBody ? "0.6rem" : "0",
            fontWeight: 400,
          }}>
            {heading.length > 120 ? heading.substring(0, 120) + "..." : heading}
          </div>
          {previewBody && (
            <div style={{
              fontFamily: "var(--sans)",
              fontSize: "0.92rem",
              fontWeight: 300,
              color: "var(--text-secondary)",
              lineHeight: 1.7,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}>
              {previewBody.length > 250 ? previewBody.substring(0, 250) + "..." : previewBody}
            </div>
          )}
        </>
      )}
      {/* Footer row with optional tags + node footer. Inaccessible nodes
          omit the footer entirely — the whole point is to not leak
          username/timestamp to viewers without pre-deletion access. */}
      {!isInaccessible && (
        <div onClick={(e) => e.stopPropagation()} style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          {footer || (
            <NodeFooter
              username={node.username}
              createdAt={node.created_at}
              childrenCount={childrenCount}
              humanOwnerUsername={node.human_owner_username}
              llmModel={node.llm_model}
              origin={node.origin}
              publicPage={node.privacy_level === 'public'}
              onReplyClick={
                !isPlaceholder && Array.isArray(actions)
                  ? (actions.find((a) => a.kind === 'reply') || {}).action || null
                  : null
              }
            />
          )}
          {!isPlaceholder && (
            <div style={{ display: "flex", alignItems: "center" }}>
              {tag && (
                <span style={{
                  ...tagStyle,
                  color: "var(--accent-dim)",
                  backgroundColor: "var(--accent-subtle)",
                }}>
                  {tag}
                </span>
              )}
              {isPinned && (
                <span style={{
                  ...tagStyle,
                  color: "var(--accent-dim)",
                  backgroundColor: "var(--accent-subtle)",
                }}>
                  Pinned
                </span>
              )}
              {promptKey ? (
                <span style={{
                  ...tagStyle,
                  color: "var(--accent-dim)",
                  backgroundColor: "var(--accent-subtle)",
                }}>
                  {promptLabel(promptKey)}
                </span>
              ) : isVoiceNote ? (
                <span style={{
                  ...tagStyle,
                  color: "var(--accent-dim)",
                  backgroundColor: "var(--accent-subtle)",
                }}>
                  Voice Note
                </span>
              ) : null}
              {canExpand && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                  aria-label={expanded ? "Collapse preview" : "Expand preview"}
                  style={{
                    marginLeft: "8px",
                    padding: "4px 8px",
                    background: "none",
                    border: "none",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: "0.85rem",
                    lineHeight: 1,
                  }}
                >
                  {expanded ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Bubble;
